import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  applyDayClick,
  isDayDisabled,
  monthsToShow,
  type SelectionState,
} from '../lib/dateRangeSelection';
import { formatDateShort } from '../lib/format';
import { addMonthsToKey, clampMonth, gridDates, monthKey } from '../lib/months';
import { todayInZurich } from '../lib/today';
import { useMonthSwipe } from '../lib/useMonthSwipe';
import { applySetFrom, applySetTo } from '../lib/useDateRange';

type Props = {
  /** YYYY-MM-DD or '' */
  from: string;
  to: string;
  onChange(from: string, to: string): void;
  /** Minimum days the end must lie after the start. 0 = single day allowed. */
  minSpanDays?: number;
  /** Inclusive selectable bounds (YYYY-MM-DD). */
  min?: string;
  max?: string;
  locale?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  placeholder?: string;
  'aria-label'?: string;
  className?: string;
};

/** Approximate desktop two-month panel width, used only for the edge flip. */
const DESKTOP_PANEL_PX = 580;

type Coords = { top: number; left?: number; right?: number };

/** The highlighted span, accounting for an in-progress (hovered) selection. */
function effectiveRange(sel: SelectionState, hovered: string): { lo: string; hi: string } {
  if (sel.selecting && sel.anchor) {
    if (hovered && hovered !== sel.anchor) {
      return sel.anchor <= hovered
        ? { lo: sel.anchor, hi: hovered }
        : { lo: hovered, hi: sel.anchor };
    }
    return { lo: sel.anchor, hi: sel.anchor };
  }
  if (sel.from && sel.to) return { lo: sel.from, hi: sel.to };
  if (sel.from) return { lo: sel.from, hi: sel.from };
  return { lo: '', hi: '' };
}

export function DateRangePicker({
  from,
  to,
  onChange,
  minSpanDays = 0,
  min,
  max,
  locale,
  required,
  disabled,
  id,
  placeholder,
  className = '',
  ...aria
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = locale ?? i18n.resolvedLanguage ?? 'de';
  const reactId = useId();
  const triggerId = id ?? reactId;

  const triggerRef = useRef<HTMLButtonElement>(null);

  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<Coords>({ top: 0, left: 0 });
  const [sel, setSel] = useState<SelectionState>({ from, to, anchor: '', selecting: false });
  const [hovered, setHovered] = useState('');
  const [viewMonth, setViewMonth] = useState(() => initialMonth(from, max));

  const ariaLabel = aria['aria-label'] ?? t('dateRange.ariaLabel');

  const close = useCallback(() => {
    setOpen(false);
    setHovered('');
    triggerRef.current?.focus();
  }, []);

  const reposition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const top = rect.bottom + 4;
    if (rect.left + DESKTOP_PANEL_PX > window.innerWidth) {
      setCoords({ top, right: Math.max(8, window.innerWidth - rect.right) });
    } else {
      setCoords({ top, left: rect.left });
    }
  }, []);

  const openPicker = useCallback(() => {
    if (disabled) return;
    setSel({ from, to, anchor: '', selecting: false });
    setHovered('');
    setViewMonth(initialMonth(from, max));
    setOpen(true);
  }, [disabled, from, to, max]);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const onDown = (e: MouseEvent) => {
      const target = e.target as Element;
      if (triggerRef.current?.contains(target)) return;
      if (target.closest?.('[data-drp-panel]')) return;
      close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, close]);

  const handleDayClick = useCallback(
    (day: string) => {
      const r = applyDayClick(sel, day, { minSpanDays, min, max });
      setSel(r.next);
      if (r.emit) {
        onChange(r.emit.from, r.emit.to);
        close();
      }
    },
    [sel, minSpanDays, min, max, onChange, close],
  );

  // Manual entry through the native date inputs — routed through the same
  // coupling transitions the app has always used, committed immediately.
  const handleManualFrom = useCallback(
    (next: string) => {
      const r = applySetFrom({ from: sel.from, to: sel.to }, next, minSpanDays);
      setSel({ from: r.from, to: r.to, anchor: '', selecting: false });
      onChange(r.from, r.to);
      if (r.from) setViewMonth(monthKey(r.from));
    },
    [sel.from, sel.to, minSpanDays, onChange],
  );

  const handleManualTo = useCallback(
    (next: string) => {
      const r = applySetTo({ from: sel.from, to: sel.to }, next, minSpanDays);
      setSel({ from: r.from, to: r.to, anchor: '', selecting: false });
      onChange(r.from, r.to);
    },
    [sel.from, sel.to, minSpanDays, onChange],
  );

  const handleClear = useCallback(() => {
    setSel({ from: '', to: '', anchor: '', selecting: false });
    setHovered('');
    onChange('', '');
  }, [onChange]);

  const minMonth = min ? monthKey(min) : undefined;
  const maxMonth = max ? monthKey(max) : undefined;
  const canPrev = !minMonth || viewMonth > minMonth;
  const canNext = !maxMonth || viewMonth < maxMonth;
  const goPrev = () => setViewMonth((m) => clampInto(addMonthsToKey(m, -1), minMonth, maxMonth));
  const goNext = () => setViewMonth((m) => clampInto(addMonthsToKey(m, 1), minMonth, maxMonth));

  const triggerText =
    from && to
      ? `${formatDateShort(from, lang)} – ${formatDateShort(to, lang)}`
      : from
        ? `${formatDateShort(from, lang)} – …`
        : (placeholder ?? t('dateRange.placeholder'));

  const range = effectiveRange(sel, hovered);

  const panelProps = {
    lang,
    from: sel.from,
    to: sel.to,
    lo: range.lo,
    hi: range.hi,
    min,
    max,
    onPick: handleDayClick,
    onHover: setHovered,
    onManualFrom: handleManualFrom,
    onManualTo: handleManualTo,
    onPrev: goPrev,
    onNext: goNext,
    canPrev,
    canNext,
    onClear: handleClear,
    t,
  };

  return (
    <div className={`relative ${className}`}>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        disabled={disabled}
        onClick={() => (open ? close() : openPicker())}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-required={required || undefined}
        className={
          'relative flex w-full max-w-full min-w-0 box-border items-center ' +
          'rounded-md border border-slate-300 bg-white pl-3 pr-9 py-2 text-left ' +
          'text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 ' +
          'disabled:opacity-50 disabled:cursor-not-allowed ' +
          'dark:border-slate-600 dark:bg-slate-900 dark:focus:ring-slate-400 ' +
          (from ? 'text-slate-800 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500')
        }
      >
        <span className="truncate">{triggerText}</span>
        <CalendarIcon />
      </button>

      {open &&
        createPortal(
          <>
            {/* Desktop: anchored two-month popover */}
            <div
              data-drp-panel
              className="hidden sm:block fixed z-[70]"
              style={{ top: coords.top, left: coords.left, right: coords.right }}
            >
              <Panel {...panelProps} months={monthsToShow(viewMonth, 2)} />
            </div>

            {/* Mobile: full-screen sheet */}
            <div
              role="dialog"
              aria-modal="true"
              aria-label={ariaLabel}
              className="sm:hidden fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 dark:bg-black/60 p-4"
              onClick={close}
            >
              <div data-drp-panel onClick={(e) => e.stopPropagation()} className="w-full max-w-xs">
                <Panel {...panelProps} months={[viewMonth]} onClose={close} swipe />
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}

type PanelProps = {
  months: string[];
  lang: string;
  from: string;
  to: string;
  lo: string;
  hi: string;
  min?: string;
  max?: string;
  onPick: (date: string) => void;
  onHover: (date: string) => void;
  onManualFrom: (next: string) => void;
  onManualTo: (next: string) => void;
  onPrev: () => void;
  onNext: () => void;
  canPrev: boolean;
  canNext: boolean;
  onClear: () => void;
  onClose?: () => void;
  swipe?: boolean;
  t: (key: string) => string;
};

function Panel({
  months,
  lang,
  from,
  to,
  lo,
  hi,
  min,
  max,
  onPick,
  onHover,
  onManualFrom,
  onManualTo,
  onPrev,
  onNext,
  canPrev,
  canNext,
  onClear,
  onClose,
  swipe,
  t,
}: PanelProps) {
  const swipeHandlers = useMonthSwipe({
    onPrev: canPrev ? onPrev : undefined,
    onNext: canNext ? onNext : undefined,
  });
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-xl dark:border-slate-700 dark:bg-slate-900">
      {/* Manual entry */}
      <div className="mb-3 flex items-end gap-2">
        <label className="flex-1 text-[11px] font-medium text-slate-500 dark:text-slate-400">
          <span className="mb-1 block">{t('dateRange.from')}</span>
          <NativeDate value={from} onChange={onManualFrom} max={to || max} min={min} />
        </label>
        <span className="pb-2 text-slate-400">–</span>
        <label className="flex-1 text-[11px] font-medium text-slate-500 dark:text-slate-400">
          <span className="mb-1 block">{t('dateRange.to')}</span>
          <NativeDate value={to} onChange={onManualTo} min={from || min} max={max} />
        </label>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('dateRange.close')}
            className="mb-0.5 rounded-md p-1.5 text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <Chevron dir="close" />
          </button>
        )}
      </div>

      {/* Month navigation */}
      <div className="mb-2 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onPrev}
          disabled={!canPrev}
          aria-label={t('dateRange.prevMonth')}
          className="rounded-md p-1.5 text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <Chevron dir="left" />
        </button>
        <div className="flex flex-1 justify-around gap-6">
          {months.map((m) => (
            <span
              key={m}
              className="flex-1 text-center text-sm font-semibold text-slate-800 dark:text-slate-100"
            >
              {monthLabel(m, lang)}
            </span>
          ))}
        </div>
        <button
          type="button"
          onClick={onNext}
          disabled={!canNext}
          aria-label={t('dateRange.nextMonth')}
          className="rounded-md p-1.5 text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <Chevron dir="right" />
        </button>
      </div>

      <div className="flex gap-6" onMouseLeave={() => onHover('')} {...(swipe ? swipeHandlers : {})}>
        {months.map((m) => (
          <MonthGrid
            key={m}
            month={m}
            lang={lang}
            lo={lo}
            hi={hi}
            min={min}
            max={max}
            onPick={onPick}
            onHover={onHover}
          />
        ))}
      </div>

      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={onClear}
          className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          {t('dateRange.clear')}
        </button>
      </div>
    </div>
  );
}

function MonthGrid({
  month,
  lang,
  lo,
  hi,
  min,
  max,
  onPick,
  onHover,
}: {
  month: string;
  lang: string;
  lo: string;
  hi: string;
  min?: string;
  max?: string;
  onPick: (date: string) => void;
  onHover: (date: string) => void;
}) {
  const cells = useMemo(() => gridDates(month), [month]);
  const today = todayInZurich();
  const weekdays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(lang, { weekday: 'short' });
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(Date.UTC(2024, 0, 1 + i))));
  }, [lang]);

  const hasRange = !!lo && !!hi && lo !== hi;

  return (
    <div className="w-[238px]">
      <div className="mb-1 grid grid-cols-7 text-center text-[11px] font-medium uppercase text-slate-400 dark:text-slate-500">
        {weekdays.map((w, i) => (
          <div key={i} className="py-1">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map(({ date, inMonth }) => {
          if (!inMonth) return <div key={date} className="h-9" />;
          const disabled = isDayDisabled(date, { min, max });
          const isLo = hasRange && date === lo;
          const isHi = hasRange && date === hi;
          const inRange = hasRange && date > lo && date < hi;
          const isEndpoint = date === lo || date === hi; // covers single-day
          const isToday = date === today;
          const dayNum = Number(date.slice(8, 10));

          const bandPos = inRange
            ? 'inset-x-0'
            : isLo
              ? 'left-1/2 right-0'
              : isHi
                ? 'left-0 right-1/2'
                : '';

          return (
            <div key={date} className="relative h-9">
              {bandPos && (
                <div
                  className={`absolute inset-y-0.5 ${bandPos} bg-slate-200 dark:bg-slate-700`}
                />
              )}
              <button
                type="button"
                disabled={disabled}
                onClick={() => onPick(date)}
                onMouseEnter={() => onHover(date)}
                aria-current={isToday ? 'date' : undefined}
                className={
                  'group relative z-10 flex h-9 w-full items-center justify-center text-sm ' +
                  (disabled ? 'cursor-default' : '')
                }
              >
                <span
                  className={
                    'flex h-8 w-8 items-center justify-center rounded-full ' +
                    (isEndpoint
                      ? 'bg-slate-900 font-semibold text-white dark:bg-slate-100 dark:text-slate-900'
                      : disabled
                        ? 'text-slate-300 dark:text-slate-600'
                        : (inRange
                            ? 'text-slate-800 dark:text-slate-100 '
                            : 'text-slate-700 dark:text-slate-200 ') +
                          'group-hover:bg-slate-100 dark:group-hover:bg-slate-800 ' +
                          (isToday ? 'ring-1 ring-slate-400 dark:ring-slate-500' : ''))
                  }
                >
                  {dayNum}
                </span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NativeDate({
  value,
  onChange,
  min,
  max,
}: {
  value: string;
  onChange: (v: string) => void;
  min?: string;
  max?: string;
}) {
  return (
    <input
      type="date"
      value={value}
      min={min}
      max={max}
      onChange={(e) => onChange(e.target.value)}
      className="block w-full max-w-full min-w-0 box-border appearance-none rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm font-normal text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:[color-scheme:dark]"
    />
  );
}

function initialMonth(from: string, max?: string): string {
  if (from) return monthKey(from);
  const today = todayInZurich();
  return max && today > max ? monthKey(max) : monthKey(today);
}

function clampInto(m: string, lo?: string, hi?: string): string {
  return clampMonth(m, lo ?? m, hi ?? m);
}

function monthLabel(ym: string, lang: string): string {
  const [y, m] = ym.split('-').map(Number);
  return new Intl.DateTimeFormat(lang, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y!, m! - 1, 1)));
}

function CalendarIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-slate-500"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function Chevron({ dir }: { dir: 'left' | 'right' | 'close' }) {
  const d =
    dir === 'left' ? 'M15 18l-6-6 6-6' : dir === 'right' ? 'M9 18l6-6-6-6' : 'M6 6l12 12M18 6L6 18';
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}
